/**
 * Bali ViewComponents - JavaScript Entry Point
 *
 * This is the main entry point for consuming Bali's JavaScript.
 * It provides access to all Stimulus controllers and utilities.
 *
 * ## Quick Start
 *
 *   import { registerAll } from 'bali-view-components'
 *   registerAll(application)
 *
 * ## Selective Imports (tree-shaking)
 *
 * Core controllers:
 *   import { DatepickerController, ModalController } from 'bali-view-components'
 *
 * Optional modules (import separately to reduce bundle size):
 *   import { ChartController, registerCharts } from 'bali-view-components/charts'
 *   import { RichTextEditorController } from 'bali-view-components/rich-text-editor'
 *
 * ## Setup in consuming app
 *
 * No bundler configuration needed. Just install the package and import.
 *
 * ### esbuild (Rails default)
 *
 *   // application.js
 *   import { Application } from '@hotwired/stimulus'
 *   import { registerAll } from 'bali-view-components'
 *
 *   const application = Application.start()
 *   registerAll(application)
 *
 * ### Vite
 *
 *   // Same imports work with Vite. May need fs.allow for gem path:
 *   server: { fs: { allow: ['.', baliGemPath] } }
 *
 * ## Usage in application.js:
 *
 *   // Register all core controllers at once
 *   import { registerAll } from 'bali-view-components'
 *   registerAll(application)
 *
 *   // Add optional modules as needed
 *   import { registerCharts } from 'bali-view-components/charts'
 *   registerCharts(application)
 */

// Re-export all utility controllers
// Import registerAll functions for the combined registerAll export
import { registerAll as registerControllers } from './controllers/index'
import { registerAll as registerComponents } from './components/index'

// Re-export individual controllers (excluding registerAll to avoid conflicts)
export {
  AutoPlayAudioController,
  AutocompleteAddressController,
  CheckboxRevealController,
  CheckboxToggleController,
  DatepickerController,
  DrawingMapsController,
  DynamicFieldsController,
  ElementsOverlapController,
  FileInputController,
  FilterPersistenceController,
  FocusOnConnectController,
  GeocoderMapsController,
  InputOnChangeController,
  InteractController,
  NumberFormatController,
  PrintController,
  RadioButtonsGroupController,
  RadioToggleController,
  SlimSelectController,
  StepNumberInputController,
  SubmitButtonController,
  SubmitOnChangeController,
  TextareaController,
  TimePeriodFieldController,
  TrixAttachmentsController
} from './controllers/index'
export { registerAll as registerAllControllers } from './controllers/index'

// Re-export all core component controllers (excludes heavy modules)
export {
  AlertController,
  AppLayoutController,
  AppliedTagsController,
  AvatarController,
  BulkActionsController,
  CarouselController,
  ChatController,
  ClipboardController,
  ColumnSelectorController,
  CommandController,
  ConditionController,
  DirectUploadController,
  DocumentEditorController,
  DocumentPageController,
  DrawerController,
  DropdownController,
  ExportLinksController,
  FeedbackWidgetController,
  FilterGroupController,
  FiltersController,
  HovercardController,
  ImageExpanderController,
  ImageFieldController,
  KanbanController,
  LocationsMapController,
  ModalController,
  MultiSelectController,
  NavbarController,
  QrScannerController,
  RateController,
  RecurrentEventRuleController,
  RevealController,
  SavedViewsController,
  SideMenuController,
  SideMenuFlyoutController,
  SideMenuTriggerController,
  SortableListController,
  SplitViewController,
  SplitViewListController,
  StatusController,
  TabsController,
  TimeagoController,
  ToastContainerController,
  ToolbarOverflowController,
  TooltipController,
  TreeViewItemController,
  WidgetGridController,
  WidgetGridEditModeController,
  WidgetRefreshController
} from './components/index'
export { registerAll as registerAllComponents } from './components/index'

// Re-export utilities
export * from './utils/index'

// Confirmation dialog (auto-installed by registerAll; exported for manual setup/opt-in)
export { confirmDialog, installConfirmDialog } from '../../assets/javascripts/bali/confirm/confirm_dialog'

/**
 * Register ALL core Bali controllers (utility + component controllers)
 *
 * NOTE: Heavy/optional modules are NOT included. Import them separately:
 *   - import { registerCharts } from 'bali-view-components/charts'
 *   - import { registerBlockEditor } from 'bali-view-components/block-editor'
 *   - import { registerRichTextEditor } from 'bali-view-components/rich-text-editor'
 *
 * @param {Application} application - Stimulus application instance
 */
export function registerAll (application) {
  registerControllers(application)
  registerComponents(application)
}
